// Product card image swap (all pages except the product page, which has its own gallery).
// Desktop: the mouse entering a card image instantly cross-fades (next alternate is preloaded when the card nears the viewport) to a random alternate gallery image,
// different from the one shown last time; leaving restores the main image.
// Touch: a long press (~400ms) does the same while held; releasing restores it and the press
// does not open the product. Cards are covered by a full-card link, so hits are resolved by
// pointer position against each card's [data-alt-images] media box.
(() => {
  if (window.__cardHoverInit) return;
  window.__cardHoverInit = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const lastPick = new WeakMap();
  let active = null;
  let pressTimer = 0;
  let pressStart = null;
  let pressActivated = false;

  const mediaAt = (target, x, y) => {
    const card = target instanceof Element ? target.closest('article') : null;
    const media = card?.querySelector('[data-alt-images]');
    if (!media) return null;
    const rect = media.getBoundingClientRect();
    return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom ? media : null;
  };

  const randomIndex = (count, avoid) => {
    let index = Math.floor(Math.random() * count);
    if (count > 1 && index === avoid) index = (index + 1 + Math.floor(Math.random() * (count - 1))) % count;
    return index;
  };

  // Pre-pick and preload the next alternate so the swap is instant on hover.
  const nextPick = new WeakMap();
  const preloaded = new Set();
  const preload = (url) => {
    if (!url || preloaded.has(url)) return;
    preloaded.add(url);
    const img = new Image();
    img.decoding = 'async';
    img.src = url;
  };
  const urlsOf = (media) => (media.dataset.altImages || '').split('|').filter(Boolean);
  const prepare = (media) => {
    const urls = urlsOf(media);
    if (!urls.length) return;
    if (!nextPick.has(media)) nextPick.set(media, randomIndex(urls.length, lastPick.get(media)));
    preload(urls[nextPick.get(media)]);
  };

  const pickUrl = (media) => {
    const urls = urlsOf(media);
    if (!urls.length) return null;
    const index = nextPick.has(media) ? nextPick.get(media) : randomIndex(urls.length, lastPick.get(media));
    lastPick.set(media, index);
    nextPick.set(media, randomIndex(urls.length, index));
    return urls[index];
  };

  // Desktop (fine pointer + hover): warm the next alternate for cards near the viewport.
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
  if (finePointer.matches && 'IntersectionObserver' in window) {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        prepare(entry.target);
      });
    }, { rootMargin: '200px 0px' });
    const watch = (root) => root.querySelectorAll?.('[data-alt-images]').forEach((media) => observer.observe(media));
    const start = () => {
      watch(document);
      new MutationObserver((records) => records.forEach((record) => record.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches('[data-alt-images]')) observer.observe(node);
        watch(node);
      }))).observe(document.body, { childList: true, subtree: true });
    };
    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  const show = (media) => {
    if (active === media) return;
    if (active) hide(active);
    const url = pickUrl(media);
    if (!url) return;
    active = media;
    const base = media.querySelector('img');
    const overlay = document.createElement('img');
    overlay.alt = '';
    overlay.setAttribute('aria-hidden', 'true');
    overlay.dataset.cardSwap = '';
    overlay.draggable = false;
    Object.assign(overlay.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      objectFit: base ? getComputedStyle(base).objectFit : 'cover',
      opacity: '0',
      pointerEvents: 'none',
      zIndex: '1',
      background: 'var(--color-surface-secondary, transparent)',
    });
    overlay.src = url;
    media.append(overlay);
    const reveal = () => {
      if (!overlay.isConnected) return;
      overlay.style.opacity = '1';
      if (!reduceMotion.matches && overlay.animate) {
        overlay.animate(
          [{ opacity: 0, transform: 'scale(1.02)', filter: 'blur(3px)' }, { opacity: 1, transform: 'scale(1)', filter: 'blur(0)' }],
          { duration: 260, easing: 'cubic-bezier(0.22, 1, 0.36, 1)' },
        );
      }
    };
    if (overlay.complete && overlay.naturalWidth) reveal();
    else (overlay.decode ? overlay.decode() : Promise.resolve()).then(reveal, reveal);
    prepare(media);
  };

  const hide = (media) => {
    if (active === media) active = null;
    media.querySelectorAll('[data-card-swap]').forEach((overlay) => {
      if (reduceMotion.matches || !overlay.animate) {
        overlay.remove();
        return;
      }
      const fade = overlay.animate([{ opacity: getComputedStyle(overlay).opacity }, { opacity: 0 }], { duration: 320, easing: 'ease-out', fill: 'forwards' });
      fade.onfinish = () => overlay.remove();
    });
  };

  // Desktop hover.
  document.addEventListener('pointermove', (event) => {
    if (event.pointerType !== 'mouse') return;
    const media = mediaAt(event.target, event.clientX, event.clientY);
    if (media) show(media);
    else if (active) hide(active);
  }, { passive: true });
  document.addEventListener('pointerleave', () => { if (active) hide(active); });
  window.addEventListener('blur', () => { if (active) hide(active); });

  // Touch long-press.
  const cancelPress = () => {
    clearTimeout(pressTimer);
    pressStart = null;
  };
  document.addEventListener('touchstart', (event) => {
    if (event.touches.length !== 1) return cancelPress();
    const touch = event.touches[0];
    const media = mediaAt(event.target, touch.clientX, touch.clientY);
    if (!media) return;
    pressActivated = false;
    pressStart = { x: touch.clientX, y: touch.clientY, media };
    clearTimeout(pressTimer);
    pressTimer = setTimeout(() => {
      pressActivated = true;
      show(media);
    }, 400);
  }, { passive: true });
  document.addEventListener('touchmove', (event) => {
    if (!pressStart || pressActivated) return;
    const touch = event.touches[0];
    if (Math.hypot(touch.clientX - pressStart.x, touch.clientY - pressStart.y) > 10) cancelPress();
  }, { passive: true });
  const endPress = () => {
    const media = pressStart?.media;
    cancelPress();
    if (pressActivated && media) hide(media);
  };
  document.addEventListener('touchend', endPress, { passive: true });
  document.addEventListener('touchcancel', endPress, { passive: true });
  // A completed long press must not open the product or the system image menu.
  document.addEventListener('click', (event) => {
    if (!pressActivated) return;
    pressActivated = false;
    event.preventDefault();
    event.stopPropagation();
  }, true);
  document.addEventListener('contextmenu', (event) => {
    if (pressStart || pressActivated) event.preventDefault();
  });
})();
